import React, { useState } from 'react';
import styled from 'styled-components';
import { colors } from '_styles';
import { _ } from '_locales';
import moment from 'moment';
import HOC from '_HOC';

const Container = styled.div`
    width: 100%;
    max-width: 580px;
    padding: 45px 35px;
    box-sizing: border-box;
    margin: 0 auto;
    min-height: 350px;
`;

const Title = styled.h3`
    font-size: 20px;
    font-weight: 400;
    font-family: Roboto, sans-serif;
    color: #000;
    text-align: center;
`;

const TimeSelectionContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
`;

const DateContainer = styled.div`
  position: relative;
`;

const Label = styled.label`
  font-size: 13px;
  position: absolute;
  background-color: #fff;
  left: 10px;
  top: 5px;
  padding: 0 5px;
  box-sizing: border-box;
  z-index: 2;
`;

const DateSelector = styled.select`
  border: 1px solid #c3c3c3;
  background-color: white;
  height: 45px;
  border-radius: 7px;
  margin-top: 15px;
  padding: 0 10px;
  box-sizing: border-box;
  appearance: none;
  width: ${({ width }) => `${width}px`};
`;

const Option = styled.option``;

const NextButton = styled.button`
  position: relative;
  font-size: 14px;
  padding: 20px 0;
  background-color: ${colors.RC_ACTION};
  cursor: pointer;
  border: none;
  border-radius: 8px;
  color: #fff;
  width: 160px;
  height: 39px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 40px auto 15px;
`;

const TimeContainer = styled.div`
  position: relative !important;
  width: 160px !important;
  max-width: 160px !important;
  height: 45px;
  top: 15px !important;
`;

const TimeInput = styled.div`
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  padding: 0 10px;
  box-sizing: border-box !important;
  width: 120px !important;
  max-width: 120px !important;
  height: 45px !important;
  align-items: center !important;
  display: flex !important;
`;

const TimeFormat = styled.div`
  position: absolute !important;
  top: 0 !important;
  right: 0 !important;
  width: 45px !important;
  max-width: 45px !important;
  border-radius: 0 7px 7px 0 !important;
  background-color: #D9D7D7 !important;
  height: 45px !important;
  justify-content: center !important;
  align-items: center !important;
  display: flex !important;
`;

const TimeBox = styled.div`
  position: relative;
`;

const TimeCheck = ({ props }) => {
  const { location, history } = props;
  const {
    title,
    isYesterday,
    curbTime
  } = location.state;

  const currentDay = new Date();
  const AmPm = (currentDay.getHours() > 12) ? 'PM' : 'AM';

  const [dateValue, setDateValue] = useState(moment().day());
  const [hourValue, setHourValue] = useState('00');
  const [minuteValue, setMinuteValue] = useState('00');
  const [formatAmPm, setFormatAmPm] = useState(AmPm);
  const [selectedDate, setSelectedDate] = useState(moment());
  const [selectedTime, setSelectedTime] = useState(
    (currentDay.getHours() > 12)
      ? `${(`0${currentDay.getHours() - 12}`).slice(-2)}:${(`0${currentDay.getMinutes()}`).slice(-2)}`
      : `${(`0${currentDay.getHours()}`).slice(-2)}:${(`0${currentDay.getMinutes()}`).slice(-2)}`
  );

  const today = moment().day();
  const yesterday = moment().subtract(1, 'day').day();
  const oneDayBeforeYesterday = moment().subtract(2, 'day').day();

  const handleChangeDate = (date) => {
    setSelectedDate(date);
    setFormatAmPm((date.getHours() > 12) ? 'PM' : 'AM');
    setSelectedTime(
      (date.getHours() > 12)
        ? `${(`0${date.getHours() - 12}`).slice(-2)}:${(`0${date.getMinutes()}`).slice(-2)}`
        : `${(`0${date.getHours()}`).slice(-2)}:${(`0${date.getMinutes()}`).slice(-2)}`
    );
  };

  const nextButtonHandler = () => {
    const formattedSelectedTime = moment(`${hourValue}:${minuteValue}:00`, 'HH:mm:ss');
    const curbTimeMoment = moment(curbTime, 'HH:mm:ss');
    const isAfterCurbTime = formattedSelectedTime.isAfter(curbTimeMoment);

    /**
     * collection is for yesterday
     * select yesterday
     * select after curb time
     */
    if (
      isYesterday
      && Number(dateValue) === yesterday
      && isAfterCurbTime
    ) {
      history.push('/help/description-page', {
        title: _('help.not_placed_timely'),
        description: _('help.materials_are_required_to_be_placed_for_pick_up_by_curb_time', { type: curbTime }),
        description_2: _('help.if_this_is_the_case_please_remove_materials_from_their_set_out_location', { type: curbTime }),
      });
      return;
    }

    /**
     * collection is for yesterday
     * select yesterday
     * select before curb time.
     */
    if (
      isYesterday
      && Number(dateValue) === yesterday
      && !isAfterCurbTime
    ) {
      history.push('/help/missed-collection-submission', { title });
      return;
    }

    /**
     * collection is for yesterday
     * select one day before yesterday
     * at any time
     */
    if (
      isYesterday
      && Number(dateValue) === oneDayBeforeYesterday
    ) {
      history.push('/help/missed-collection-submission', { title });
    }

    /**
     * collection is for yesterday
     * select today
     * at any time
     */
    if (
      isYesterday
      && Number(dateValue) === today
    ) {
      history.push('/help/description-page', {
        title: _('help.not_placed_timely'),
        description: _('help.materials_are_required_to_be_placed_for_pick_up_by_curb_time', { type: curbTime }),
        description_2: _('help.if_this_is_the_case_please_remove_materials_from_their_set_out_location', { type: curbTime }),
      });
    }

    /**
     * collection is for yesterday
     * select any other time
     * at any time
     */
    if (
      isYesterday
      && Number(dateValue) !== today
      && Number(dateValue) !== yesterday
      && Number(dateValue) !== oneDayBeforeYesterday
    ) {
      history.push('/help/missed-collection-submission', { title });
    }

    // --------------------------------------------------------------

    /**
     * collection is for today
     * select yesterday
     * at any time
     */
    if (
      !isYesterday
      && Number(dateValue) === yesterday
    ) {
      history.push('/help/missed-collection-submission', { title });
    }

    /**
     * collection is for today
     * select today
     * select after curb time
     */
    if (
      !isYesterday
      && Number(dateValue) === today
      && isAfterCurbTime
    ) {
      history.push('/help/description-page', {
        title: _('help.not_placed_timely'),
        description: _('help.materials_are_required_to_be_placed_for_pick_up_by_curb_time', { type: curbTime }),
        description_2: _('help.if_this_is_the_case_please_remove_materials_from_their_set_out_location', { type: curbTime }),
      });
    }

    /**
     * collection is for today
     * select today
     * select before curb time
     */
    if (
      !isYesterday
      && Number(dateValue) === today
      && !isAfterCurbTime
    ) {
      history.push('/help/missed-collection-submission', { title });
    }

    /**
     * collection is for today
     * select any other time
     * at any time
     */
    if (
      !isYesterday
      && Number(dateValue) !== today
      && Number(dateValue) !== yesterday
    ) {
      history.push('/help/missed-collection-submission', { title });
    }
  };

  return (
    <Container>
      <Title>{_('help.what_time_were_materials_set_out_for_pick_up')}</Title>

      <TimeSelectionContainer>

        <DateContainer>
          <Label htmlFor="date_selector">{_('help.day')}</Label>
          <DateSelector width={110} id="date_selector" value={dateValue} onChange={({ target }) => setDateValue(target.value)}>
            <Option value={0}>{_('zone.sun')}</Option>
            <Option value={1}>{_('zone.mon')}</Option>
            <Option value={2}>{_('zone.tue')}</Option>
            <Option value={3}>{_('zone.wed')}</Option>
            <Option value={4}>{_('zone.thu')}</Option>
            <Option value={5}>{_('zone.fri')}</Option>
            <Option value={6}>{_('zone.sat')}</Option>
          </DateSelector>
        </DateContainer>

        <DateContainer>
          <Label htmlFor="date_selector">{_('help.hour')}</Label>
          <DateSelector width={70} id="date_selector" value={hourValue} onChange={({ target }) => setHourValue(target.value)}>
            <Option value="00">00</Option>
            <Option value="01">01</Option>
            <Option value="02">02</Option>
            <Option value="03">03</Option>
            <Option value="04">04</Option>
            <Option value="05">05</Option>
            <Option value="06">06</Option>
            <Option value="07">07</Option>
            <Option value="08">08</Option>
            <Option value="09">09</Option>
            <Option value="10">10</Option>
            <Option value="11">11</Option>
            <Option value="12">12</Option>
            <Option value="13">13</Option>
            <Option value="14">14</Option>
            <Option value="15">15</Option>
            <Option value="16">16</Option>
            <Option value="17">17</Option>
            <Option value="18">18</Option>
            <Option value="19">19</Option>
            <Option value="20">20</Option>
            <Option value="21">21</Option>
            <Option value="22">22</Option>
            <Option value="23">23</Option>
          </DateSelector>
        </DateContainer>

        <DateContainer>
          <Label htmlFor="date_selector">{_('help.minute')}</Label>
          <DateSelector width={70} id="date_selector" value={minuteValue} onChange={({ target }) => setMinuteValue(target.value)}>
            <Option value="00">00</Option>
            <Option value="15">15</Option>
            <Option value="30">30</Option>
            <Option value="45">45</Option>
          </DateSelector>
        </DateContainer>
      </TimeSelectionContainer>

      <NextButton onClick={() => nextButtonHandler()}>{_('help.next')}</NextButton>
    </Container>
  );
};

export default HOC(TimeCheck);
